import type { UrlLocation } from '@seek/chalice-types';
import type { Language, Country as MelwaysCountry } from '@seek/melways-sites';
import { canonicaliseUrl, generateAlternateLinks } from '@seek/seek-jobs-seo';

import type { Country } from 'src/types/globals';

import type { TypedAction, TypedThunkAction } from '../types';

import { SET_ALTERNATE_LINKS, SET_CANONICAL_URL, type SeoState } from './types';

export const initialState: SeoState = {
  canonicalUrl: null,
  partners: {
    canCrossLink: false,
  },
};

const reducer = (
  state: SeoState = initialState,
  action: TypedAction,
): SeoState => {
  switch (action.type) {
    case SET_CANONICAL_URL: {
      const { canonical: canonicalUrl } = action.payload;

      return { ...state, canonicalUrl };
    }

    case SET_ALTERNATE_LINKS: {
      const { alternateLinks } = action.payload;

      return { ...state, alternateLinks };
    }

    default:
      return state;
  }
};

export const setAlternateLinks =
  ({
    country,
    path,
    query,
  }: {
    country: Country;
    path: string;
    query: UrlLocation['query'];
  }): TypedThunkAction =>
  (dispatch, getState) => {
    const {
      results: resultsStore,
      appConfig: { site },
    } = getState();
    const { location, canonicalCompany, companySuggestion } = resultsStore;

    const alternateLinks: Partial<Record<Language | 'zh-HK', string>> =
      generateAlternateLinks({
        country: country.toLocaleLowerCase() as MelwaysCountry,
        path,
        query: query || {},
        location,
        searchMeta: { canonicalCompany, companySuggestion },
        site,
      }) || {};

    dispatch({
      type: SET_ALTERNATE_LINKS,
      payload: {
        alternateLinks,
      },
    });
  };

export const setCanonicalUrl =
  ({
    country,
    path,
    query,
  }: {
    country: Country;
    path: string;
    query: UrlLocation['query'];
  }): TypedThunkAction =>
  (dispatch, getState) => {
    const { results: resultsStore } = getState();
    const { location, canonicalCompany, companySuggestion } = resultsStore;

    const canonicalisation = canonicaliseUrl({
      country: country.toLocaleLowerCase() as MelwaysCountry,
      path,
      query: query || {},
      location,
      searchMeta: { canonicalCompany, companySuggestion },
    });

    dispatch({
      type: SET_CANONICAL_URL,
      payload: canonicalisation,
    });
  };

export default reducer;
